// internal-deps
import { getBridgeSdk, GlobalHeaderState, SwgyHeaderKeysList } from "@swiggy-private/checkout-core";
import { isEmpty } from "@swiggy-private/core-ui";
import { CartItem } from "@swiggy-private/cart-types";

// constants
import { CLEVERTAP_KEYS } from "@cart-client/constants/Clevertap";

// interfaces
import { ReviewedCart } from "@cart-client/interfaces/Cart";

// helpers
import { getCartItemsCount } from "./Cart";
import { isUserLoggedIn } from "./User";
import { getSelectedSLA } from "./Header";

const getMealType = (cartItems: CartItem[]): string => {
    let mealType = 1;
    cartItems.map((item) => {
        // if cart item already have non veg item don't override it
        if (mealType == 0) {
            return;
        }
        // if it's a egg or non veg meal update the meal type
        if (item.isVeg == 2 || item.isVeg == 0) {
            mealType = item.isVeg;
        }
    });

    switch (mealType) {
        case 1:
            return "Veg";
        case 2:
            return "Egg";
        default:
            return "Non Veg";
    }
};

const getBoltSLA = (cartData: ReviewedCart) => {
    console.log("");
    if (!cartData?.data?.is_bolt_cart) return null;

    const userAddress = cartData.data?.addresses[0];

    return userAddress?.sla_options
        ? getSelectedSLA(userAddress.sla_options, userAddress.selected_sla_option ?? "")
        : null;
};

export const logCartViewedEvent = (cartData: ReviewedCart): void => {
    if (isEmpty(cartData.data)) {
        return;
    }
    let superPlanType = "";
    const cartMenuItems = [...(cartData.data.cart_menu_items ?? [])];

    const subsItems = cartData.data.subscription_items ?? [];
    const cartItems = cartMenuItems.sort((itemA, itemB) => itemB.final_price - itemA.final_price);

    const itemsQuantity = getCartItemsCount(cartData);

    subsItems.map((item) => {
        superPlanType += item.name;
    });

    const taxAndCharges = (cartData.data.rendering_details ?? []).find(
        (item) => item.key === "taxes_and_charges",
    )?.value;

    const swiggyOneBenefitsApplicable = cartData.data.swiggyOneBenefitsApplicable ?? false;
    const swiggyOneExclusiveDiscount = cartData.data.swiggyOneExclusiveDiscount ?? 0;
    const swiggyOneExclusiveRewardValue = cartData.data.swiggyOneExclusiveRewardValue ?? 0;

    const eoUnlocked = swiggyOneBenefitsApplicable && swiggyOneExclusiveDiscount > 0;
    const freeDelUnlocked =
        swiggyOneBenefitsApplicable && cartData.data.free_delivery_discount && cartData.data.free_delivery_discount > 0;

    const isCouponApplied = cartData.data.coupon_details?.is_coupon_valid == true;
    const couponPotentialSavingsStr = cartData.data.apply_coupon_widget_info?.savings ?? "₹0";
    const couponPotentialSavings = Number(couponPotentialSavingsStr.slice(1)); // Eg. ₹120 -> 120
    const superSpecificDiscount = Math.floor(cartData.data.super_specific_discount ?? 0);
    const totalDiscount = cartData.data.discount_message_v2?.message_details?.total_discount ?? 0;
    const totalPotentialSavings = isCouponApplied ? totalDiscount : couponPotentialSavings + superSpecificDiscount;

    const clevertapParams: { [key: string]: unknown } = {
        [CLEVERTAP_KEYS.RESTAURANT_NAME]: cartData.data.restaurant_details?.name,
        [CLEVERTAP_KEYS.RESTAURANT_ID]: cartData.data.restaurant_details?.id,
        [CLEVERTAP_KEYS.RESTAURANT_AREA]: cartData.data.restaurant_details?.area_name,
        // TODO
        [CLEVERTAP_KEYS.ITEM_TOTAL]: cartData.data.item_total,
        [CLEVERTAP_KEYS.TAXES_AND_CHARGES]: taxAndCharges ?? "NULL",
        [CLEVERTAP_KEYS.DEL_FEE]: cartData.data.delivery_charges,
        [CLEVERTAP_KEYS.SURGE_FEE]: cartData.data.special_fee,
        [CLEVERTAP_KEYS.TOTAL_DISCOUNT]: cartData.data.discount_total,
        [CLEVERTAP_KEYS.TOTAL_BILL_VALUE]: cartData.data.cart_total,
        [CLEVERTAP_KEYS.COUPON_APPLIED_FLAG]: isCouponApplied ? "Yes" : "No",
        [CLEVERTAP_KEYS.COUPON_NAME]:
            cartData.data.coupon_details?.is_coupon_valid == true ? cartData.data.coupon_code : "NULL",
        [CLEVERTAP_KEYS.IS_SWIGGY_ONE]: cartData.data.user_super_status_details?.user_status === "SUPER",
        [CLEVERTAP_KEYS.QUANTITY]: itemsQuantity,
        [CLEVERTAP_KEYS.MEAL_TYPE]: getMealType(cartItems),
        [CLEVERTAP_KEYS.ADDRESS_ID]: cartData.data.address_id ?? "0",
        [CLEVERTAP_KEYS.CART_ID]: cartData.data.cartId ?? "0",
        [CLEVERTAP_KEYS.ONE_PLAN_ADDED]: subsItems.length > 0,
        [CLEVERTAP_KEYS.ONE_PLAN_DURATION_ADDED]: isEmpty(superPlanType) ? "NULL" : superPlanType,
        [CLEVERTAP_KEYS.EO_UNLOCKED]: eoUnlocked,
        [CLEVERTAP_KEYS.EO_DISCOUNT_SAVINGS]: eoUnlocked ? Math.floor(swiggyOneExclusiveDiscount) : 0,
        [CLEVERTAP_KEYS.EO_DISCOUNT_PERCENTAGE]: eoUnlocked ? swiggyOneExclusiveRewardValue : 0,
        [CLEVERTAP_KEYS.ONE_FREE_DELIVERY_UNLOCKED]: freeDelUnlocked,
        [CLEVERTAP_KEYS.ONE_FREE_DELIVERY_SAVINGS]: Math.floor(cartData.data.free_delivery_discount ?? 0),
        [CLEVERTAP_KEYS.ONE_SAVINGS]: Math.floor(cartData.data.super_specific_discount ?? 0),
        [CLEVERTAP_KEYS.COUPON_CODE_CART]: cartData.data.apply_coupon_widget_info?.coupon_code ?? "NULL",
        [CLEVERTAP_KEYS.COUPON_DISCOUNT_SAVINGS]: cartData.data.apply_coupon_widget_info?.savings ?? "NULL",
        [CLEVERTAP_KEYS.TOTAL_POTENTIAL_SAVINGS]: totalPotentialSavings,
        [CLEVERTAP_KEYS.IS_GUEST_USER]: !isUserLoggedIn(),
        [CLEVERTAP_KEYS.IS_BOLT_CART]: cartData?.data?.is_bolt_cart ?? false,
        [CLEVERTAP_KEYS.BOLT_SLA]: getBoltSLA(cartData) ?? "NULL",
        xChannel: GlobalHeaderState.getHeader(SwgyHeaderKeysList.xChannel) || "",
    };

    // top 5 cart items to be added in list
    cartItems.map((item, index) => {
        if (index < 5) {
            clevertapParams[`item ${index + 1} name`] = item.name ?? "NULL";
            clevertapParams[`item ${index + 1} quantity`] = item.quantity.toString() ?? "NULL";
            clevertapParams[`item ${index + 1} id`] = item.menu_item_id.toString() ?? "NULL";
        }
    });

    getBridgeSdk().logClevertapEvent({ eventName: CLEVERTAP_KEYS.CART_VIEWED_EVENT, params: clevertapParams });
};

export const logCouponAppliedEvent = (cartData: ReviewedCart): void => {
    const deviceId = GlobalHeaderState.getHeader(SwgyHeaderKeysList.deviceId) || "NULL";
    const clevertapParams: { [key: string]: unknown } = {
        [CLEVERTAP_KEYS.SWUID]: deviceId,
        [CLEVERTAP_KEYS.STATUS]: "TRUE",
        [CLEVERTAP_KEYS.MESSAGE]: cartData.data?.coupon_details?.coupon_success_card?.description,
        [CLEVERTAP_KEYS.COUPON_CODE]: cartData.data?.coupon_code,
        [CLEVERTAP_KEYS.SAVINGS_AMOUNT]: cartData.data?.coupon_discount_total,
        [CLEVERTAP_KEYS.IS_BOLT_CART]: cartData?.data?.is_bolt_cart ?? false,
        [CLEVERTAP_KEYS.BOLT_SLA]: getBoltSLA(cartData) ?? "NULL",
        xChannel: GlobalHeaderState.getHeader(SwgyHeaderKeysList.xChannel) || "",
    };

    getBridgeSdk().logClevertapEvent({ eventName: CLEVERTAP_KEYS.CART_COUPON_APPLIED_EVENT, params: clevertapParams });
};
